/**
 * useQrCameraScanner (MUI version)
 * ---------------------------------------------------------------------------
 * A ready-to-use React hook that opens the device camera inside an MUI
 * Dialog and scans QR codes in real time using jsQR.
 *
 * Install dependencies first:
 *   npm install jsqr @mui/material @mui/icons-material @emotion/react @emotion/styled
 *
 * Usage:
 * ------
 * import { useQrCameraScanner } from './useQrCameraScanner';
 *
 * function MyComponent() {
 *   const { openScanner, QrScannerModal } = useQrCameraScanner({
 *     onScan: (value) => console.log('Scanned:', value),
 *   });
 *
 *   return (
 *     <div>
 *       <Button onClick={openScanner}>Scan QR Code</Button>
 *       <QrScannerModal />
 *     </div>
 *   );
 * }
 * ---------------------------------------------------------------------------
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import jsQR from 'jsqr';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Alert,
  Box,
  Stack,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

export function useQrCameraScanner(
  options
) {
  const {
    onScan,
    facingMode = 'environment',
    closeOnScan = true,
    scanIntervalMs = 66,
    title = 'Scan QR Code',
  } = options;

  const [isOpen, setIsOpen] = useState(false);
  const [error, setError] = useState(null);

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(null);
  const lastScanTimeRef = useRef(0);
  const onScanRef = useRef(onScan);
  onScanRef.current = onScan; // always call the latest callback

  const stopStream = useCallback(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  }, []);

  const openScanner = useCallback(() => {
    setError(null);
    setIsOpen(true);
  }, []);

  const closeScanner = useCallback(() => {
    setIsOpen(false);
    stopStream();
  }, [stopStream]);

  // A "session" id lets us safely ignore stale async work from a previous
  // open/close cycle (e.g. if the dialog is closed while getUserMedia is
  // still resolving).
  const sessionRef = useRef(0);

  function decodeFrame() {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    if (video.readyState !== video.HAVE_ENOUGH_DATA) return;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);

    const code = jsQR(imageData.data, imageData.width, imageData.height, {
      inversionAttempts: 'dontInvert',
    });

    if (code && code.data) {
      onScanRef.current(code.data);
    //   if (closeOnScan) {
    //     closeScanner();
    //   }
    }
  }

  function tick(session, timestamp = 0) {
    if (session !== sessionRef.current) return; // a newer/older session, stop

    if (timestamp - lastScanTimeRef.current >= scanIntervalMs) {
      lastScanTimeRef.current = timestamp;
      decodeFrame();
    }

    rafRef.current = requestAnimationFrame((t) => tick(session, t));
  }

  // Called by the Dialog ONLY once it has fully mounted and finished its
  // enter transition — this guarantees the <video> DOM node exists and has
  // a real layout size, which avoids the "camera opens but nothing renders"
  // race you get from starting the stream on a plain `isOpen` effect.
  const handleDialogEntered = useCallback(async () => {
    const session = ++sessionRef.current;

    if (!navigator.mediaDevices?.getUserMedia) {
      setError('Camera access is not supported in this browser.');
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode },
        audio: false,
      });

      if (session !== sessionRef.current) {
        // Dialog was closed again before we got the stream.
        stream.getTracks().forEach((t) => t.stop());
        return;
      }

      streamRef.current = stream;

      const video = videoRef.current;
      if (!video) {
        setError('Video element not found.');
        stream.getTracks().forEach((t) => t.stop());
        return;
      }

      video.srcObject = stream;

      // Wait for real dimensions before we start drawing frames.
      await new Promise((resolve) => {
        if (video.readyState >= video.HAVE_METADATA) {
          resolve();
        } else {
          video.onloadedmetadata = () => resolve();
        }
      });

      await video.play();
      rafRef.current = requestAnimationFrame((t) => tick(session, t));
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Unable to access camera.';
      setError(message);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facingMode, scanIntervalMs, closeOnScan]);

  // Called once the Dialog has fully closed/animated out.
  const handleDialogExited = useCallback(() => {
    sessionRef.current += 1; // invalidate any in-flight camera setup
    stopStream();
  }, [stopStream]);

  // Safety net: stop the camera if the component using the hook unmounts
  // entirely while the dialog was still open.
  useEffect(() => {
    return () => {
      sessionRef.current += 1;
      stopStream();
    };
  }, [stopStream]);

  const QrScannerModal= useCallback(() => {
    return (
      <Dialog
        open={isOpen}
        onClose={closeScanner}
        maxWidth="xs"
        fullWidth
        aria-labelledby="qr-scanner-dialog-title"
        TransitionProps={{
          onEntered: handleDialogEntered,
          onExited: handleDialogExited,
        }}
      >
        <DialogTitle
          id="qr-scanner-dialog-title"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            pr: 1,
          }}
        >
          {title}
          <IconButton
            onClick={closeScanner}
            aria-label="Close scanner"
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <Stack spacing={2}>
            {error ? (
              <Alert severity="error">{error}</Alert>
            ) : (
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  aspectRatio: '1 / 1',
                  borderRadius: 2,
                  overflow: 'hidden',
                  bgcolor: 'black',
                }}
              >
                <Box
                  component="video"
                  ref={videoRef}
                  muted
                  playsInline
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: 'block',
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    top: '15%',
                    left: '15%',
                    right: '15%',
                    bottom: '15%',
                    border: '2px solid rgba(255,255,255,0.85)',
                    borderRadius: 2,
                    pointerEvents: 'none',
                  }}
                />
              </Box>
            )}

            {/* Hidden canvas used purely for frame decoding */}
            <canvas ref={canvasRef} style={{ display: 'none' }} />

            {!error && (
              <Typography
                variant="body2"
                color="text.secondary"
                align="center"
              >
                Point your camera at a QR code
              </Typography>
            )}
          </Stack>
        </DialogContent>
      </Dialog>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, error, title, closeScanner, handleDialogEntered, handleDialogExited]);

  return { isOpen, openScanner, closeScanner, error, QrScannerModal };
}